/**
 * P8 — in-memory cache of MULTI WP details=YOA Map（内部仍用 byYearOfAssessment 键名）。
 * Used so Tab edits stay in the Map and Save can submit all YOAs at once.
 */
import { hydrateWpSignoffFromPayload, useWpSignoffStore } from '@/store/audit/wpSignoffStore';

export type WpYoaBucket = {
  details?: any;
  glInvoiceDetails?: any;
  [key: string]: any;
};

export type WpYoaMapCacheEntry = {
  companyId: number;
  annualId: number;
  code: string;
  byYearOfAssessment: Record<string, WpYoaBucket>;
  /** Engagement YOA order (from GET keys) */
  yoaOrder: string[];
  /** PL-TURN-SI/SE MULTI：与 YOA 同级的共享 annualInfo（Tab 投影时挂回） */
  annualInfo?: Record<string, unknown> | null;
  wpStatus?: number | null;
  wpIssueRemark?: string | null;
  outstanding?: boolean | null;
  outstandingRemark?: string | null;
  issueStatus?: number | null;
};

const WP_META_KEYS = new Set([
  'wpStatus',
  'wpIssueRemark',
  'glInvoiceDetails',
  'SME-FRS',
  'WOKE_DONE',
  'autoAdjustments',
  'outstanding',
  'outstandingRemark',
  'issueStatus',
  'spanType',
  'annualInfo',
  'preparedBy',
  'reviewedBy',
  'preparedDate',
  'reviewedDate',
  'preparedUserIds',
  'reviewedUserIds',
]);

const store = new Map<string, WpYoaMapCacheEntry>();

export function wpYoaMapCacheKey(companyId: number, annualId: number, code: string): string {
  return `${companyId}:${annualId}:${String(code || '').trim().toUpperCase()}`;
}

export function setWpYoaMapCache(entry: WpYoaMapCacheEntry): void {
  const key = wpYoaMapCacheKey(entry.companyId, entry.annualId, entry.code);
  store.set(key, entry);
}

export function getWpYoaMapCache(
  companyId: number,
  annualId: number,
  code: string,
): WpYoaMapCacheEntry | null {
  return store.get(wpYoaMapCacheKey(companyId, annualId, code)) || null;
}

export function clearWpYoaMapCache(companyId: number, annualId: number, code: string): void {
  store.delete(wpYoaMapCacheKey(companyId, annualId, code));
}

/** Write current Tab projected detail back into the YOA bucket. */
export function upsertWpYoaBucketDetails(
  companyId: number,
  annualId: number,
  code: string,
  yoa: string,
  projectedDetail: any,
): void {
  const trimmed = String(yoa || '').trim();
  if (!trimmed || !projectedDetail || typeof projectedDetail !== 'object') return;
  const existing = getWpYoaMapCache(companyId, annualId, code);
  if (!existing) return;

  const detailFields: Record<string, any> = {};
  let glInvoiceDetails: any = undefined;
  let wpStatus: any = undefined;
  let wpIssueRemark: any = undefined;

  for (const [k, v] of Object.entries(projectedDetail)) {
    if (k === 'glInvoiceDetails') {
      glInvoiceDetails = v;
    } else if (k === 'wpStatus') {
      wpStatus = v;
    } else if (k === 'wpIssueRemark') {
      wpIssueRemark = v;
    } else if (!WP_META_KEYS.has(k)) {
      detailFields[k] = v;
    }
  }

  const prev = existing.byYearOfAssessment[trimmed] || {};
  // BS-EQ-IC only: merge patch into prior bag so tab drafts that write {shareholders}
  // alone do not wipe shared registeredInfo/directors/holdingCompany.
  // Other WPs keep original replace semantics.
  const isIc = String(code || '').trim().toUpperCase() === 'BS-EQ-IC';
  let nextDetails: any[];
  if (isIc) {
    let prevBag: Record<string, any> = {};
    if (Array.isArray(prev.details) && prev.details[0] && typeof prev.details[0] === 'object') {
      prevBag = { ...prev.details[0] };
    } else if (prev.details && typeof prev.details === 'object' && !Array.isArray(prev.details)) {
      prevBag = { ...prev.details };
    }
    nextDetails = [{ ...prevBag, ...detailFields }];
  } else {
    nextDetails = [detailFields];
  }
  existing.byYearOfAssessment[trimmed] = {
    ...prev,
    details: nextDetails,
    ...(glInvoiceDetails !== undefined ? { glInvoiceDetails } : {}),
  };
  if (wpStatus !== undefined) existing.wpStatus = wpStatus;
  if (wpIssueRemark !== undefined) existing.wpIssueRemark = wpIssueRemark;
  store.set(wpYoaMapCacheKey(companyId, annualId, code), { ...existing });
}

/**
 * Build batchSaveOrUpdate body value for one whitelist code:
 * { [yoa]: { details }, ..., issueMark? }
 */
export function buildYoaMapBatchPayload(
  companyId: number,
  annualId: number,
  code: string,
  issueMark?: { marked: boolean; remark?: string } | null,
): Record<string, any> | null {
  const entry = getWpYoaMapCache(companyId, annualId, code);
  if (!entry || entry.yoaOrder.length === 0) return null;

  // BS-EQ-IC MULTI only (≥2 YOAs): inverted wire. SINGLE / 1-slice / other codes unchanged.
  if (String(code || '').trim().toUpperCase() === 'BS-EQ-IC' && entry.yoaOrder.length >= 2) {
    return buildIssuedCapitalInvertedBatchPayload(entry, issueMark);
  }

  const payload: Record<string, any> = {};
  for (const yoa of entry.yoaOrder) {
    const bucket = entry.byYearOfAssessment[yoa] || {};
    let detailsObj: Record<string, any> = {};
    if (Array.isArray(bucket.details) && bucket.details.length > 0
      && bucket.details[0] && typeof bucket.details[0] === 'object') {
      detailsObj = { ...bucket.details[0] };
    } else if (bucket.details && typeof bucket.details === 'object' && !Array.isArray(bucket.details)) {
      detailsObj = { ...bucket.details };
    }
    for (const mk of WP_META_KEYS) {
      delete detailsObj[mk];
    }
    const signoffPatch = useWpSignoffStore.getState().toSavePatch({
      companyId,
      annualId,
      code,
      yearOfAssessment: yoa,
    });
    payload[yoa] = { details: { ...detailsObj, ...signoffPatch, yearOfAssessment: yoa } };
  }
  if (issueMark) {
    payload.issueMark = issueMark;
  }
  return payload;
}

/** BS-EQ-IC MULTI wire: { details: [{ shareholders: {yoa→[]}, ... }], issueMark? } */
function buildIssuedCapitalInvertedBatchPayload(
  entry: WpYoaMapCacheEntry,
  issueMark?: { marked: boolean; remark?: string } | null,
): Record<string, any> {
  const shareholders: Record<string, any[]> = {};
  let registeredInfo: any = {};
  let directors: any[] = [];
  let holdingCompany: any = {};
  let deletedDirectorIds: any = undefined;
  let directorRemovalActions: any = undefined;
  let wokeDone: any = undefined;

  for (const yoa of entry.yoaOrder) {
    const bucket = entry.byYearOfAssessment[yoa] || {};
    let bag: Record<string, any> = {};
    if (Array.isArray(bucket.details) && bucket.details.length > 0
      && bucket.details[0] && typeof bucket.details[0] === 'object') {
      bag = { ...bucket.details[0] };
    } else if (bucket.details && typeof bucket.details === 'object' && !Array.isArray(bucket.details)) {
      bag = { ...bucket.details };
    }
    shareholders[yoa] = Array.isArray(bag.shareholders) ? bag.shareholders : [];
    if (bag.registeredInfo != null) registeredInfo = bag.registeredInfo;
    if (Array.isArray(bag.directors)) directors = bag.directors;
    if (bag.holdingCompany != null) holdingCompany = bag.holdingCompany;
    if (bag.deletedDirectorIds != null) deletedDirectorIds = bag.deletedDirectorIds;
    if (bag.directorRemovalActions != null) directorRemovalActions = bag.directorRemovalActions;
    if (bag['WOKE_DONE'] != null) wokeDone = bag['WOKE_DONE'];
  }

  const inner: Record<string, any> = {
    shareholders,
    registeredInfo,
    directors,
    holdingCompany,
  };
  if (deletedDirectorIds != null) inner.deletedDirectorIds = deletedDirectorIds;
  if (directorRemovalActions != null) inner.directorRemovalActions = directorRemovalActions;
  if (wokeDone != null) inner['WOKE_DONE'] = wokeDone;

  const payload: Record<string, any> = {
    details: [inner],
  };
  if (issueMark) payload.issueMark = issueMark;
  return payload;
}

/** Project one YOA bucket to the WorkingPaperData shape components expect. */
export function projectYoaBucketToWorkingPaperData(
  entry: WpYoaMapCacheEntry,
  yoa: string | null | undefined,
): any {
  const order = entry.yoaOrder;
  const key = (typeof yoa === 'string' && yoa.trim() && entry.byYearOfAssessment[yoa.trim()])
    ? yoa.trim()
    : (order[0] || Object.keys(entry.byYearOfAssessment)[0] || '');
  const bucket = entry.byYearOfAssessment[key] || {};
  const details = bucket.details;
  let projected: any;
  if (Array.isArray(details) && details.length > 0
    && details[0] && typeof details[0] === 'object' && !Array.isArray(details[0])) {
    projected = { ...details[0] };
  } else if (details && typeof details === 'object' && !Array.isArray(details)) {
    projected = { ...details };
  } else {
    projected = {};
  }
  if (bucket.glInvoiceDetails != null) {
    projected.glInvoiceDetails = bucket.glInvoiceDetails;
  }
  if (entry.wpStatus != null) projected.wpStatus = entry.wpStatus;
  if (entry.wpIssueRemark != null) projected.wpIssueRemark = entry.wpIssueRemark;
  if (entry.outstanding != null) projected.outstanding = entry.outstanding;
  if (entry.outstandingRemark != null) projected.outstandingRemark = entry.outstandingRemark;
  if (entry.issueStatus != null) projected.issueStatus = entry.issueStatus;
  // PL-TURN-SI/SE：共享 annualInfo 挂回活动片，避免切 Tab 丢失 Nature & Scope
  if (entry.annualInfo != null && typeof entry.annualInfo === 'object') {
    projected.annualInfo = entry.annualInfo;
  }
  const signoffKeys = ['preparedBy', 'reviewedBy', 'preparedDate', 'reviewedDate', 'preparedUserIds', 'reviewedUserIds'] as const;
  for (const signoffKey of signoffKeys) {
    if (bucket[signoffKey] != null) projected[signoffKey] = bucket[signoffKey];
  }
  if (entry.companyId > 0 && entry.annualId > 0 && entry.code) {
    hydrateWpSignoffFromPayload(
      {
        companyId: entry.companyId,
        annualId: entry.annualId,
        code: entry.code,
        yearOfAssessment: key,
      },
      projected as Record<string, unknown>,
    );
  }
  return projected;
}
